<template>
	<layout-login>
		<main class="login-page">
			<section class="login-shell">
				<aside class="login-showcase" :style="companyBgUrl">
					<div class="showcase-overlay"></div>
					<div class="showcase-content">
						<div class="brand-mark"><v-icon color="white">verified_user</v-icon></div>
						<div class="brand-copy">
							<span class="brand-eyebrow">QUIPUSOFT SA · ACCESS CONTROL</span>
							<h1>Control de acceso<br>simple y seguro.</h1>
							<p>Administra usuarios, permisos e integraciones desde un solo lugar.</p>
						</div>
						<div class="security-features">
							<div><v-icon color="white">admin_panel_settings</v-icon><span>Permisos centralizados</span></div>
							<div><v-icon color="white">history</v-icon><span>Trazabilidad de accesos</span></div>
							<div><v-icon color="white">security</v-icon><span>Sesiones protegidas</span></div>
						</div>
					</div>
				</aside>

				<section class="login-access" aria-labelledby="login-title">
					<div class="mobile-brand">
						<span><v-icon color="white">verified_user</v-icon></span> ACL · QUIPUSOFT SA
					</div>
					<div class="login-card">
						<header class="login-heading">
							<span class="status-pill"><i></i> Acceso seguro</span>
							<h2 id="login-title">Bienvenido</h2>
							<p>Ingresa tus credenciales para continuar al panel administrativo.</p>
						</header>

						<form id="login-form" class="login-form" @submit.prevent="signin">
							<label for="login-email">Correo electrónico</label>
							<div class="field-control">
								<v-icon>person_outline</v-icon>
								<input id="login-email" v-model.trim="user" type="email"
									placeholder="nombre@empresa.com" autocomplete="username"
									:disabled="isLoading" required>
							</div>
							<label for="login-password">Contraseña</label>
							<div class="field-control">
								<v-icon>lock_outline</v-icon>
								<input id="login-password" v-model="password"
									:type="showPassword ? 'text' : 'password'"
									placeholder="Ingresa tu contraseña" autocomplete="current-password"
									:disabled="isLoading" required>
								<button type="button" class="password-toggle"
									:aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
									@click="showPassword = !showPassword">
									<v-icon>{{ showPassword ? 'visibility_off' : 'visibility' }}</v-icon>
								</button>
							</div>
							<button class="login-button" type="submit" :disabled="isLoading">
								<span v-if="!isLoading">Ingresar al sistema</span>
								<span v-else class="loading-label"><i></i> Verificando acceso...</span>
								<v-icon v-if="!isLoading" color="white">arrow_forward</v-icon>
							</button>
						</form>
						<div class="login-note"><v-icon>lock</v-icon> Tus credenciales se transmiten mediante una conexión segura.</div>
					</div>
					<p class="copyright">© {{ currentYear }} QUIPUSOFT S.A. · Todos los derechos reservados</p>
				</section>
			</section>
		</main>
	</layout-login>
</template>

<script>
async function signin() {
	window.localStorage.clear();
	this.isLoading = true;
	const userData = {
		email: this.user,
		password: this.password,
		codeApp: process.env.APP_CODE,
		provider: 4,
	};
	try {
		const response = await this.$http.post('signin/auth', userData);
		const user = response.data.data;
		const inLocalStorage = JSON.stringify(user);
		const { token, codeProject } = user;
		localStorage.setItem(process.env.STORAGE_USER_KEY, inLocalStorage);
		localStorage.setItem(`${process.env.STORAGE_USER_KEY}::codeProject`, codeProject);
		localStorage.setItem(`${process.env.STORAGE_USER_KEY}::token`, token);
		this.$store.dispatch('setToken', token);
		this.goTo('page-project');
	} finally {
		this.isLoading = false;
	}
}

function data() {
	return {
		companyBgUrl: process.env.COMPANY_LOGIN_BACKGROUND
			? `background-image: url(${process.env.COMPANY_LOGIN_BACKGROUND})` : '',
		user: '',
		password: '',
		isLoading: false,
		showPassword: false,
		currentYear: new Date().getFullYear(),
	};
}

export default {
	name: 'page-login',
	data,
	methods: { signin },
};
</script>

<style lang="scss" scoped>
.login-page {
	align-items: center;
	background: radial-gradient(circle at 12% 15%, rgba(26, 67, 56, .10), transparent 28%),
		radial-gradient(circle at 88% 82%, rgba(46, 107, 89, .08), transparent 24%), #f4f7fb;
	display: flex;
	font-family: map-get($fonts, open-regular);
	justify-content: center;
	min-height: 100vh;
	padding: 28px;
}
.login-shell {
	background: white;
	border: 1px solid rgba(148, 163, 184, .22);
	border-radius: 24px;
	box-shadow: 0 28px 70px rgba(15, 23, 42, .14);
	display: grid;
	grid-template-columns: minmax(400px, 1.05fr) minmax(440px, .95fr);
	height: calc(100vh - 56px);
	max-height: 680px;
	max-width: 1160px;
	min-height: 560px;
	overflow: hidden;
	width: 100%;
}
.login-showcase {
	background-color: #12372E;
	background-position: center;
	background-size: cover;
	color: white;
	position: relative;
}
.showcase-overlay {
	background: linear-gradient(145deg, rgba(18, 55, 46, .98), rgba(26, 67, 56, .92));
	inset: 0;
	position: absolute;
}
.showcase-content {
	display: flex;
	flex-direction: column;
	height: 100%;
	justify-content: space-between;
	padding: 54px;
	position: relative;
}
.brand-mark {
	align-items: center;
	background: rgba(255, 255, 255, .14);
	border: 1px solid rgba(255, 255, 255, .24);
	border-radius: 16px;
	display: flex;
	height: 52px;
	justify-content: center;
	width: 52px;
	.icon { font-size: 28px; }
}
.brand-copy {
	margin: auto 0;
	h1 {
		font-family: map-get($fonts, open-semibold);
		font-size: 48px;
		letter-spacing: -.04em;
		line-height: 1.12;
		margin: 0 0 24px;
	}
	p { color: rgba(255, 255, 255, .78); font-size: 17px; line-height: 1.7; margin: 0; max-width: 430px; }
}
.brand-eyebrow {
	color: #BFD5CC;
	display: block;
	font-size: 12px;
	font-weight: 700;
	letter-spacing: .16em;
	margin-bottom: 18px;
}
.security-features {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
	div {
		align-items: center;
		background: rgba(255, 255, 255, .1);
		border: 1px solid rgba(255, 255, 255, .14);
		border-radius: 10px;
		display: flex;
		font-size: 12px;
		gap: 8px;
		padding: 9px 12px;
	}
	.icon {
		color: #EAF3EF !important;
		font-size: 17px;
		text-shadow: 0 1px 2px rgba(0, 0, 0, .16);
	}
}
.login-access {
	align-items: center;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 56px 68px 34px;
}
.login-card { max-width: 420px; width: 100%; }
.mobile-brand { display: none; }
.login-heading {
	margin-bottom: 36px;
	h2 {
		color: #0f172a;
		font-family: map-get($fonts, open-semibold);
		font-size: 34px;
		letter-spacing: -.035em;
		margin: 16px 0 8px;
	}
	p { color: #64748b; font-size: 14px; line-height: 1.6; margin: 0; }
}
.status-pill {
	align-items: center;
	background: #F4F8F6;
	border-radius: 999px;
	color: #1A4338;
	display: inline-flex;
	font-size: 11px;
	font-weight: 700;
	gap: 7px;
	letter-spacing: .04em;
	padding: 7px 11px;
	text-transform: uppercase;
	i { background: #22c55e; border-radius: 50%; box-shadow: 0 0 0 3px rgba(34, 197, 94, .14); height: 7px; width: 7px; }
}
.login-form label { color: #334155; display: block; font-size: 13px; font-weight: 600; margin: 0 0 8px; }
.field-control {
	align-items: center;
	background: #f8fafc;
	border: 1px solid #dbe3ed;
	border-radius: 11px;
	display: flex;
	height: 54px;
	margin-bottom: 22px;
	padding: 0 15px;
	transition: .2s ease;
	&:focus-within { background: white; border-color: #2E6B59; box-shadow: 0 0 0 4px rgba(46, 107, 89, .12); }
	> .icon { color: #94a3b8; font-size: 21px; margin-right: 12px; }
	input {
		background: transparent;
		border: 0;
		color: #0f172a;
		font-family: inherit;
		font-size: 14px;
		height: 100%;
		min-width: 0;
		outline: none;
		width: 100%;
		&::placeholder { color: #94a3b8; }
	}
}
.password-toggle {
	align-items: center;
	background: transparent;
	border: 0;
	border-radius: 7px;
	cursor: pointer;
	display: flex;
	height: 34px;
	justify-content: center;
	outline: none;
	width: 34px;
	&:hover, &:focus { background: #EEF5F2; }
	.icon { color: #64748b; font-size: 20px; }
}
.login-button {
	align-items: center;
	background: linear-gradient(135deg, #245B4C, #1A4338);
	border: 0;
	border-radius: 11px;
	box-shadow: 0 10px 22px rgba(26, 67, 56, .24);
	color: white;
	cursor: pointer;
	display: flex;
	font-family: inherit;
	font-size: 14px;
	font-weight: 600;
	height: 54px;
	justify-content: center;
	margin-top: 8px;
	outline: none;
	transition: .2s ease;
	width: 100%;
	.icon { font-size: 19px; margin-left: 10px; }
	&:hover:not(:disabled) { box-shadow: 0 13px 26px rgba(26, 67, 56, .32); transform: translateY(-1px); }
	&:disabled { cursor: wait; opacity: .75; }
}
.loading-label {
	align-items: center;
	display: flex;
	gap: 10px;
	i { animation: spin .8s linear infinite; border: 2px solid rgba(255, 255, 255, .35); border-radius: 50%; border-top-color: white; height: 17px; width: 17px; }
}
.login-note {
	align-items: center;
	border-top: 1px solid #e8edf3;
	color: #8492a6;
	display: flex;
	font-size: 11px;
	gap: 8px;
	line-height: 1.5;
	margin-top: 30px;
	padding-top: 22px;
	.icon { color: #94a3b8; font-size: 15px; }
}
.copyright { color: #94a3b8; font-size: 11px; margin: auto 0 0; padding-top: 40px; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-height: 760px) and (min-width: 901px) {
	.login-page { padding: 18px; }
	.login-shell {
		height: calc(100vh - 36px);
		min-height: 540px;
	}
	.showcase-content { padding: 28px 46px; }
	.brand-mark { border-radius: 13px; height: 46px; width: 46px; }
	.brand-copy {
		h1 { font-size: 42px; margin-bottom: 18px; }
		p { font-size: 15px; line-height: 1.55; }
	}
	.brand-eyebrow { margin-bottom: 13px; }
	.security-features { gap: 8px; }
	.security-features div { padding: 7px 10px; }
	.login-access { padding: 30px 56px 22px; }
	.login-heading {
		margin-bottom: 26px;
		h2 { font-size: 31px; margin-top: 12px; }
	}
	.field-control { height: 50px; margin-bottom: 17px; }
	.login-button { height: 50px; }
	.login-note { margin-top: 22px; padding-top: 18px; }
	.copyright { padding-top: 20px; }
}

@media (max-height: 575px) and (min-width: 901px) {
	.login-page { align-items: flex-start; overflow-y: auto; }
	.login-shell { height: 540px; }
}

@media (max-width: 900px) {
	.login-page { padding: 18px; }
	.login-shell { grid-template-columns: 1fr; height: auto; max-height: none; max-width: 560px; min-height: auto; }
	.login-showcase { display: none; }
	.login-access { min-height: calc(100vh - 36px); padding: 42px 52px 28px; }
	.mobile-brand {
		align-items: center;
		color: #1A4338;
		display: flex;
		font-size: 17px;
		font-weight: 700;
		gap: 11px;
		margin-bottom: 58px;
		max-width: 420px;
		width: 100%;
		span { align-items: center; background: linear-gradient(135deg, #2E6B59, #12372E); border-radius: 10px; display: flex; height: 38px; justify-content: center; width: 38px; }
		.icon { font-size: 22px; }
	}
}
@media (max-width: 520px) {
	.login-page { background: white; padding: 0; }
	.login-shell { border: 0; border-radius: 0; box-shadow: none; min-height: 100vh; }
	.login-access { min-height: 100vh; padding: 32px 24px 24px; }
	.mobile-brand { margin-bottom: 44px; }
	.login-heading { margin-bottom: 30px; h2 { font-size: 30px; } }
}
</style>



// WEBPACK FOOTER //
// src/pages/page-login.vue